import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ElementsHome from './ElementsHome';
import ElementsCategoryView from './ElementsCategoryView';
import ElementsSearchView from './ElementsSearchView';
import { ELEMENT_ITEMS } from '../../data/elementsCatalog';
import useRecentElements from '../../hooks/useRecentElements';
import './elementsPanel.css';

const chipToCategory = {
  shape: { id: 'shape', title: 'Shapes' },
  line: { id: 'line', title: 'Lines' },
  frame: { id: 'frame', title: 'Frames' },
  arrow: { id: 'arrow', title: 'Arrows' },
};

const ElementsPanel = ({ onInsertElement, firebase, franchiseeId }) => {
  const [view, setView] = useState('home');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeChip, setActiveChip] = useState(null);
  const [query, setQuery] = useState('');
  const { recentIds, pushRecent } = useRecentElements(firebase, franchiseeId);
  const [toastMessage, setToastMessage] = useState('');

  const handleSelectCategory = useCallback((category, sourceChip = null) => {
    setActiveCategory(category);
    setActiveChip(sourceChip);
    setView('category');
  }, []);

  const handleChipClick = useCallback((chipId) => {
    const category = chipToCategory[chipId];
    if (!category) return;
    handleSelectCategory(category, chipId);
  }, [handleSelectCategory]);

  const handleBack = useCallback(() => {
    setActiveCategory(null);
    setActiveChip(null);
    setView('home');
  }, []);

  const normalizedQuery = query.trim();
  const itemsById = useMemo(
    () => new Map(ELEMENT_ITEMS.map((item) => [item.catalogId, item])),
    [],
  );
  const recentItems = useMemo(
    () => recentIds.map((id) => itemsById.get(id)).filter(Boolean),
    [itemsById, recentIds],
  );

  const handleActivateItem = useCallback((item) => {
    if (!item) return;
    if (item.disabled) {
      setToastMessage('Coming soon');
      return;
    }
    pushRecent(item.catalogId);
    if (onInsertElement) {
      onInsertElement(item);
      setToastMessage('Added to canvas');
    } else {
      setToastMessage('Insert coming next step');
    }
  }, [onInsertElement, pushRecent]);

  useEffect(() => {
    if (normalizedQuery.length > 0 && view !== 'search') {
      setView('search');
      return;
    }
    if (normalizedQuery.length === 0 && view === 'search') {
      if (activeCategory) {
        setView('category');
      } else {
        setView('home');
      }
    }
  }, [normalizedQuery, view, activeCategory]);

  useEffect(() => {
    if (!toastMessage) return;
    const timeout = window.setTimeout(() => setToastMessage(''), 1800);
    return () => window.clearTimeout(timeout);
  }, [toastMessage]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (view === 'search' && normalizedQuery.length > 0) {
        setQuery('');
        return;
      }
      if (view === 'category') {
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleBack, normalizedQuery.length, view]);

  const content = useMemo(() => {
    if (view === 'category' && activeCategory) {
      return (
        <ElementsCategoryView
          category={activeCategory}
          activeChip={activeChip}
          onBack={handleBack}
          onActivateItem={handleActivateItem}
          recentItems={recentItems}
        />
      );
    }
    if (view === 'search') {
      return (
        <ElementsSearchView
          query={normalizedQuery}
          onActivateItem={handleActivateItem}
        />
      );
    }
    return (
      <ElementsHome
        query={query}
        onQueryChange={setQuery}
        activeChip={activeChip}
        onChipClick={handleChipClick}
        onSelectCategory={handleSelectCategory}
        recentItems={recentItems}
        onActivateItem={handleActivateItem}
      />
    );
  }, [
    activeCategory,
    activeChip,
    handleActivateItem,
    handleBack,
    handleChipClick,
    handleSelectCategory,
    normalizedQuery,
    query,
    recentItems,
    view,
  ]);

  return (
    <div className="studio-elements-panel" role="region" aria-label="Elements panel">
      {content}
      {toastMessage && (
        <div className="elements-toast" role="status">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default ElementsPanel;
